import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Loads one calendar month of entries, re-fetching when the month, the user, or
 * `refreshKey` changes.
 *
 * @param {(month: number, year: number) => Promise<Object[]>} fetcher
 *   Must be stable across renders — pass a module-level export such as
 *   `getExpenses`, not an inline arrow. Held in a ref so an accidentally-inline
 *   fetcher degrades to "stale function" rather than an infinite refetch loop.
 * @param {number} month 1-12
 * @param {number} year
 * @param {number} refreshKey Bumped by any mutation to re-sync.
 * @param {string} errorMessage Shown to the user if the fetch fails.
 * @returns {{ entries: Object[], setEntries: Function, error: string }}
 *   `setEntries` is exposed so pages can splice their local list optimistically
 *   after an edit or delete without waiting for a refetch.
 */
export const useMonthlyEntries = (fetcher, month, year, refreshKey, errorMessage) => {
  const { user } = useAuth();
  // Keyed on the id, not the object: Supabase hands back a fresh user object on
  // every auth event, so depending on identity would refetch every list on each
  // hourly token refresh.
  const userId = user?.id;
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState('');

  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  // The `cancelled` flag guards against a slower response for an earlier month
  // landing after a faster one when the user navigates months quickly.
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    fetcherRef.current(month, year)
      .then((data) => {
        if (cancelled) return;
        setEntries(data);
        // Cleared on success, so the error banner's Retry is just a refresh().
        setError('');
      })
      .catch((caught) => {
        if (cancelled) return;
        console.error(caught);
        setError(errorMessage);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, month, year, refreshKey, errorMessage]);

  return { entries, setEntries, error };
};

export default useMonthlyEntries;
